<template>
  <div>
    <Head :title="title"/>

    <NotificationOverlay/>

    <div class="min-h-full h-[calc(100vh-12rem)]">
      <div class="bg-gray-800 pb-32">
        <Disclosure as="nav" class="bg-gray-800" v-slot="{ open }">
          <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div class="border-b border-gray-700">
              <div class="flex h-16 items-center justify-between px-4 sm:px-0">
                <div class="flex items-center">
                  <inertia-link href="/" class="flex-shrink-0">
                    <img class="h-8 w-8" src="https://cdn.livck.com/livck/icon.svg"
                         alt="LIVCK.com"/>
                  </inertia-link>
                  <div class="hidden md:block">
                    <div class="ml-10 flex items-baseline space-x-4">
                      <inertia-link v-for="item in navigation" :key="item.name" :href="item.href"
                         :class="[item.current ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white', 'rounded-md px-3 py-2 text-sm font-medium']"
                         :aria-current="item.current ? 'page' : undefined">{{ __(item.name) }}</inertia-link>
                    </div>
                  </div>
                </div>
                <div class="hidden md:flex space-x-2 items-center">
                  <language-selector :colorScheme="'dark'"/>
                  <div class="ml-4 flex items-center md:ml-6">
                    <jet-dropdown align="right" width="48">
                      <template #trigger>
                        <div class="flex items-center space-x-3 text-gray-300 hover:bg-gray-700 hover:text-white rounded-md px-3 py-2 cursor-pointer">
                          <div>
                            <img class="inline-block h-9 w-9 rounded-md" :src="$page.props.user.profile_photo_url" alt=""/>
                          </div>
                          <div class="text-left hidden sm:block">
                            <p class="text-sm font-semibold text-gray-200 group-hover:text-white">{{
                                $page.props.user.name
                              }}</p>
                            <p class="text-xs font-medium text-gray-300 group-hover:text-white">{{
                                $page.props.user.email
                              }}</p>
                          </div>
                        </div>
                      </template>

                      <template #content>
                        <!-- Account Management -->
                        <div class="block px-4 py-2 text-xs text-gray-400">
                          {{ __('Manage Account') }}
                        </div>

                        <jet-dropdown-link :href="route('profile.show')">
                          {{ __('Profile') }}
                        </jet-dropdown-link>

                        <jet-dropdown-link :href="'/nova'" :active="route().current('api-tokens.index')" v-if="can('*')">
                          {{ __('Administration') }}
                        </jet-dropdown-link>

                        <jet-dropdown-link :href="route('api-tokens.index')" v-if="$page.props.jetstream.hasApiFeatures">
                          {{ __('API Tokens') }}
                        </jet-dropdown-link>

                        <div class="border-t border-gray-100"></div>

                        <!-- Authentication -->
                        <form @submit.prevent="logout">
                          <jet-dropdown-link as="button">
                            {{ __('Log Out') }}
                          </jet-dropdown-link>
                        </form>
                      </template>
                    </jet-dropdown>
                  </div>
                </div>
                <div class="flex md:hidden">
                  <!-- Hamburger -->
                  <div class="flex items-center sm:hidden">
                    <button @click="showingNavigationDropdown = ! showingNavigationDropdown" class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-900 focus:outline-none transition">
                      <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                        <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden">
            <div class="pt-2 pb-3 space-y-1">
              <jet-responsive-nav-link :href="route('dashboard')" :active="route().current('dashboard')">
                {{ __('Overview') }}
              </jet-responsive-nav-link>
              <jet-responsive-nav-link :href="route('billing.transaction.list')" :active="route().current('billing.transaction.list')">
                {{ __('Billing') }}
              </jet-responsive-nav-link>
            </div>

            <!-- Responsive Settings Options -->
            <div class="pt-4 pb-1 border-y border-gray-700">
              <div class="flex items-center px-4">
                <div v-if="$page.props.jetstream.managesProfilePhotos" class="shrink-0 mr-3" >
                  <img class="h-10 w-10 rounded-md object-cover" :src="$page.props.user.profile_photo_url" :alt="$page.props.user.name" />
                </div>

                <div>
                  <div class="font-medium text-base text-white">{{ $page.props.user.name }}</div>
                  <div class="font-medium text-sm text-gray-400">{{ $page.props.user.email }}</div>
                </div>
              </div>

              <div class="mt-3 space-y-1">
                <jet-responsive-nav-link :href="route('profile.show')" :active="route().current('profile.show')">
                  {{ __('Profile') }}
                </jet-responsive-nav-link>

                <jet-responsive-nav-link :href="'/nova'" :active="route().current('api-tokens.index')" v-if="can('*')">
                  {{ __('Administration') }}
                </jet-responsive-nav-link>

                <jet-responsive-nav-link :href="route('api-tokens.index')" :active="route().current('api-tokens.index')" v-if="$page.props.jetstream.hasApiFeatures">
                  {{ __('API Tokens') }}
                </jet-responsive-nav-link>

                <!-- Authentication -->
                <form method="POST" @submit.prevent="logout">
                  <jet-responsive-nav-link as="button">
                    {{ __('Log Out') }}
                  </jet-responsive-nav-link>
                </form>

              </div>
            </div>
          </div>
        </Disclosure>
        <header v-if="$slots.header" class="py-10">
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <slot name="header"/>
          </div>
        </header>
      </div>

      <main class="-mt-36">
        <div class="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8 mb-12">
          <slot/>
        </div>
      </main>
    </div>

  </div>
</template>

<script>
import {defineComponent} from 'vue'
import JetApplicationMark from '@/Jetstream/ApplicationMark.vue'
import JetBanner from '@/Jetstream/Banner.vue'
import JetDropdown from '@/Jetstream/Dropdown.vue'
import JetDropdownLink from '@/Jetstream/DropdownLink.vue'
import JetNavLink from '@/Jetstream/NavLink.vue'
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue'
import {Head, Link} from '@inertiajs/inertia-vue3';
import {Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue'
import NotificationOverlay from "@/Misc/NotificationOverlay";
import XFooter from "@/Layouts/Guest/Footer";
import LanguageSelector from "@/Layouts/Misc/LanguageSelector";

export default defineComponent({
  props: {
    title: String,
  },

  components: {
    LanguageSelector,
    NotificationOverlay,
    Head,
    JetApplicationMark,
    JetBanner,
    JetDropdown,
    JetDropdownLink,
    JetNavLink,
    JetResponsiveNavLink,
    Link,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    XFooter
  },

  data() {
    return {
      showingNavigationDropdown: false,
      navigation: [
        { name: 'Overview', href: route('dashboard'), current: route().current('dashboard') },
        { name: 'Billing', href: route('billing.transaction.list'), current: route().current('billing.transaction.list') },
      ],
    }
  },

  methods: {
    logout() {
      this.$inertia.post(route('logout'));
    },
  }
})
</script>

<style scoped>
.a{fill:#0f192a;}.b{fill:#00e091;}
</style>